// A newer Charlie build is live. THE APP NEVER RELOADS ITSELF (Shane, 2026-10-02: with a deploy every few minutes the old auto-reload made every click "reset"). This module only
// notices: when the app comes back to the foreground (and every ten minutes) it asks the server for index.html (never cached), compares the main bundle it names with the one this page
// is running, and tells whoever is listening (the small "A new version is available" toast) that a newer build exists. Nothing here ever calls reload except reloadNow(), which runs
// only when Shane taps Refresh, and saves where he is (route, scroll) first so the page comes back to the same place.
const SEEN = 'charlie.update.shownAt';
const VIEW = 'charlie.update.view';
export const MIN_GAP_MS = 60 * 60 * 1000; // the toast shows at most once an hour

export const bundleOf = (html) => { const m = /\/static\/js\/(main\.[A-Za-z0-9]+\.js)/.exec(html || ''); return m ? m[1] : null; };
export const runningBundle = (doc = document) => {
  const tag = Array.from(doc.querySelectorAll('script[src]')).map((s) => s.getAttribute('src')).find((u) => /\/static\/js\/main\.[A-Za-z0-9]+\.js/.test(u || ''));
  return tag ? bundleOf(tag) : null;
};

// ---- the tiny store the toast listens to ----
let pending = null; // the newer bundle name, once seen
const listeners = new Set();
export const pendingUpdate = () => pending;
export function onUpdateAvailable(cb) { listeners.add(cb); if (pending) cb(pending); return () => listeners.delete(cb); }
export function setPendingUpdate(latest) { if (latest && latest !== pending) { pending = latest; listeners.forEach((cb) => cb(latest)); } }
export const resetUpdateState = () => { pending = null; listeners.clear(); };

// 'current' | 'available' | 'offline' | 'unknown'. Never reloads.
export async function checkForNewBuild({ fetchImpl = fetch, running = runningBundle(), setPending = setPendingUpdate } = {}) {
  if (!running) return 'unknown'; // a dev server: nothing to compare
  try {
    const res = await fetchImpl('/index.html', { cache: 'no-store' });
    const latest = bundleOf(await res.text());
    if (!latest || latest === running) return 'current';
    setPending(latest);
    return 'available';
  } catch (e) {
    return 'offline';
  }
}

// ---- may the toast show right now? Once an hour at most. ----
export function mayShowToast(now = Date.now(), storage = typeof window !== 'undefined' ? window.localStorage : null) {
  try { const last = Number(storage.getItem(SEEN) || 0); return !last || now - last >= MIN_GAP_MS; } catch (e) { return true; }
}
export function markToastShown(now = Date.now(), storage = typeof window !== 'undefined' ? window.localStorage : null) {
  try { storage.setItem(SEEN, String(now)); } catch (e) { /* ignore */ }
}

// ---- keep his place across a reload he asked for ----
export function saveViewState({ win = window, storage = win.sessionStorage, now = Date.now() } = {}) {
  try {
    const scroller = win.document.querySelector('.charlie-main-content');
    storage.setItem(VIEW, JSON.stringify({ hash: win.location.hash, scrollTop: scroller ? scroller.scrollTop : 0, at: now }));
  } catch (e) { /* ignore */ }
}
export function restoreViewState({ win = window, storage = win.sessionStorage, now = Date.now(), delay = 700 } = {}) {
  let v = null;
  try { v = JSON.parse(storage.getItem(VIEW) || 'null'); storage.removeItem(VIEW); } catch (e) { return false; }
  if (!v || now - v.at > 2 * 60 * 1000 || v.hash !== win.location.hash) return false; // the route itself is in the address; scroll is restored only on the same page
  setTimeout(() => { const s = win.document.querySelector('.charlie-main-content'); if (s) s.scrollTop = v.scrollTop || 0; }, delay);
  return true;
}
export function reloadNow(win = window) { saveViewState({ win }); win.location.reload(); }

export function installAppUpdate() {
  if (typeof document === 'undefined') return;
  restoreViewState();
  const run = () => {
    if (document.visibilityState !== 'visible') return;
    checkForNewBuild();
    // Looking for a new service worker is harmless (the worker never reloads the page); it just keeps the next load fresh.
    if (navigator.serviceWorker && navigator.serviceWorker.getRegistration) navigator.serviceWorker.getRegistration().then((r) => r && r.update()).catch(() => {});
  };
  document.addEventListener('visibilitychange', run);
  window.addEventListener('pageshow', run);
  setTimeout(run, 4000); // shortly after launch
  setInterval(run, 10 * 60 * 1000);
}
